import React, { useState, useEffect, useCallback } from "react";
import "./CustomerReviews.css";

const API_BASE = "https://xiadot.com/vendor_api/vendor/get_reviews.php";
const token = () => localStorage.getItem("vendor_token");

function StarDisplay({ rating, size = 16 }) {
  return (
    <span className="cr-stars" style={{ fontSize: size }}>
      {[1,2,3,4,5].map(s => (
        <span key={s} className={s <= rating ? "cr-star-filled" : "cr-star-empty"}>★</span>
      ))}
    </span>
  );
}

function RatingBar({ label, count, total }) {
  const pct = total ? Math.round((count / total) * 100) : 0;
  return (
    <div className="cr-bar-row">
      <span className="cr-bar-label">{label}★</span>
      <div className="cr-bar-track">
        <div className="cr-bar-fill" style={{ width: `${pct}%` }} />
      </div>
      <span className="cr-bar-count">{count}</span>
    </div>
  );
}

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const d = Math.floor(diff / 86400000);
  if (d === 0) return "Today";
  if (d === 1) return "Yesterday";
  if (d < 30) return `${d}d ago`;
  if (d < 365) return `${Math.floor(d/30)}mo ago`;
  return `${Math.floor(d/365)}y ago`;
}

// ── Media Popup ───────────────────────────────────────────────
function MediaPopup({ media, onClose }) {
  return (
    <div className="cr-popup-overlay" onClick={onClose}>
      <div className="cr-popup-box" onClick={e => e.stopPropagation()}>
        <button className="cr-popup-close" onClick={onClose}>✕</button>
        <h3 className="cr-popup-title">Customer Photos / Videos</h3>
        <div className="cr-popup-grid">
          {media.map((item, i) => {
            const isVideo = item.url?.match(/\.(mp4|webm|mov)$/i);
            return isVideo ? (
              <video key={i} src={item.url} controls className="cr-popup-media" />
            ) : (
              <img key={i} src={item.url} alt={`media-${i}`} className="cr-popup-media" />
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Review Card ───────────────────────────────────────────────
function ReviewCard({ review }) {
  const [showMediaPopup, setShowMediaPopup] = useState(false);

  const ratingColor = review.rating >= 4 ? "#16a34a" : review.rating === 3 ? "#d97706" : "#ef4444";

  let mediaItems = [];
  try {
    mediaItems = review.media
      ? (typeof review.media === "string" ? JSON.parse(review.media) : review.media)
      : [];
  } catch(e) { mediaItems = []; }

  const hasMedia = Array.isArray(mediaItems) && mediaItems.length > 0;

  return (
    <div className="cr-card cr-card-compact">
      <div className="cr-card-header">
        <div className="cr-avatar cr-avatar-sm">
          {review.user_name?.[0]?.toUpperCase() || "?"}
        </div>
        <div className="cr-card-meta">
          <span className="cr-customer-name">{review.user_name}</span>
          <span className="cr-date">{timeAgo(review.created_at)}</span>
        </div>
        <div className="cr-card-right">
          <div className="cr-rating-badge" style={{ background: ratingColor }}>
            {review.rating}★
          </div>
        </div>
      </div>

      {review.product_name && (
        <div className="cr-product-tag cr-product-tag-sm">
          <span>🛍️ {review.product_name}</span>
        </div>
      )}

      <p className="cr-review-body cr-review-body-sm">{review.review}</p>

      {/* View button - only if media exists */}
      {hasMedia && (
        <button className="cr-view-btn" onClick={() => setShowMediaPopup(true)}>
          📷 View Photos/Videos
        </button>
      )}

      {review.vendor_reply && (
        <div className="cr-reply-box">
          <div className="cr-reply-label"><span>🏪</span> Your Reply</div>
          <p className="cr-reply-text">{review.vendor_reply}</p>
        </div>
      )}

      {showMediaPopup && (
        <MediaPopup media={mediaItems} onClose={() => setShowMediaPopup(false)} />
      )}
    </div>
  );
}

// ── Filter Form ───────────────────────────────────────────────
function FilterForm({ allReviews, onFilter }) {
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [selectedProduct, setSelectedProduct] = useState("all");

  // Get unique products from reviews
  const products = [...new Map(
    allReviews
      .filter(r => r.product_name)
      .map(r => [r.product_id, { id: r.product_id, name: r.product_name }])
  ).values()];

  const handleFilter = () => {
  let filtered = [...allReviews];

  if (fromDate) {
    const from = new Date(fromDate);
    from.setHours(0, 0, 0, 0);
    filtered = filtered.filter(r => new Date(r.created_at) >= from);
  }
  if (toDate) {
    const to = new Date(toDate);
    to.setHours(23, 59, 59, 999);
    filtered = filtered.filter(r => new Date(r.created_at) <= to);
  }
  if (selectedProduct !== "all") {
    filtered = filtered.filter(r => String(r.product_id) === String(selectedProduct));
  }

  onFilter(filtered);
};

  const handleReset = () => {
    setFromDate("");
    setToDate("");
    setSelectedProduct("all");
    onFilter(allReviews);
  };

  return (
    <div className="cr-filter-form">
      <div className="cr-filter-row">
        <div className="cr-filter-field">
          <label>From Date</label>
          <input
            type="date"
            value={fromDate}
            onChange={e => setFromDate(e.target.value)}
            className="cr-date-input"
          />
        </div>
        <div className="cr-filter-field">
          <label>To Date</label>
          <input
            type="date"
            value={toDate}
            onChange={e => setToDate(e.target.value)}
            className="cr-date-input"
          />
        </div>
        <div className="cr-filter-field cr-filter-field-wide">
          <label>Product</label>
          <select
            value={selectedProduct}
            onChange={e => setSelectedProduct(e.target.value)}
            className="cr-product-select"
          >
            <option value="all">All Products</option>
            {products.map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>
        <div className="cr-filter-btns">
          <button className="cr-apply-btn" onClick={handleFilter}>🔍 Filter</button>
          <button className="cr-reset-btn" onClick={handleReset}>↺ Reset</button>
        </div>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────
export default function CustomerReviews() {
  const [allReviews, setAllReviews]     = useState([]);
  const [filtered, setFiltered]         = useState([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState("");
  const [ratingFilter, setRating]       = useState(0);
  const [overallSummary, setSummary]    = useState(null);

  const fetchReviews = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const res = await fetch(API_BASE, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      const text = await res.text();
      const json = JSON.parse(text);
      if (!json.success) throw new Error(json.message);

      const rows = json.rows || [];
      setAllReviews(rows);
      setFiltered(rows);

      if (rows.length > 0) {
        const avg = rows.reduce((s, r) => s + Number(r.rating), 0) / rows.length;
        setSummary({ total: rows.length, avg: avg.toFixed(1) });
      } else {
        setSummary({ total: 0, avg: "0.0" });
      }
    } catch(e) {
      setError(e.message || "Failed to load reviews.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchReviews(); }, [fetchReviews]);

  const ratingFiltered = ratingFilter === 0
    ? filtered
    : filtered.filter(r => Number(r.rating) === ratingFilter);

  const ratingCounts = [5,4,3,2,1].map(s => ({
    star: s,
    count: filtered.filter(r => Number(r.rating) === s).length,
  }));

  return (
    <div className="cr-page">
      <div className="cr-page-header">
        <div>
          <h1 className="cr-page-title">Customer Reviews</h1>
          <p className="cr-page-sub">See what customers say about your products</p>
        </div>
        <button className="cr-refresh-btn" onClick={fetchReviews} disabled={loading}>
          {loading ? "…" : "↻ Refresh"}
        </button>
      </div>

      {/* Summary */}
      {overallSummary && (
        <div className="cr-summary-row">
          <div className="cr-summary-card cr-summary-big">
            <span className="cr-big-num">{overallSummary.avg}</span>
            <StarDisplay rating={Math.round(overallSummary.avg)} size={22} />
            <span className="cr-big-label">Overall Rating</span>
          </div>
          <div className="cr-summary-card">
            <span className="cr-summary-num">{overallSummary.total}</span>
            <span className="cr-summary-label">Total Reviews</span>
          </div>
          <div className="cr-summary-card">
            <span className="cr-summary-num">{allReviews.filter(r => r.vendor_reply).length}</span>
            <span className="cr-summary-label">Replied</span>
          </div>
          <div className="cr-summary-card">
            <span className="cr-summary-num">{allReviews.filter(r => !r.vendor_reply).length}</span>
            <span className="cr-summary-label">Awaiting Reply</span>
          </div>
        </div>
      )}

      {/* Filter Form */}
      {!loading && allReviews.length > 0 && (
        <FilterForm allReviews={allReviews} onFilter={setFiltered} />
      )}

      <div className="cr-content-row">
        <aside className="cr-sidebar">
          <div className="cr-sidebar-section">
            <h3 className="cr-sidebar-title">Filter by Rating</h3>
            <button className={`cr-filter-btn ${ratingFilter === 0 ? "active" : ""}`}
              onClick={() => setRating(0)}>All Reviews</button>
            {[5,4,3,2,1].map(s => (
              <button key={s}
                className={`cr-filter-btn ${ratingFilter === s ? "active" : ""}`}
                onClick={() => setRating(s)}>
                {"★".repeat(s)}{"☆".repeat(5-s)} ({ratingCounts.find(r=>r.star===s)?.count||0})
              </button>
            ))}
          </div>

          {filtered.length > 0 && (
            <div className="cr-sidebar-section">
              <h3 className="cr-sidebar-title">Rating Breakdown</h3>
              {[5,4,3,2,1].map(s => (
                <RatingBar key={s} label={s}
                  count={ratingCounts.find(r=>r.star===s)?.count||0}
                  total={filtered.length} />
              ))}
            </div>
          )}
        </aside>

        <main className="cr-main">
          {error && <div className="cr-error">{error}</div>}
          {loading && (
            <div className="cr-loading-grid">
              {[1,2,3].map(i => <div key={i} className="cr-skeleton" />)}
            </div>
          )}
          {!loading && ratingFiltered.length === 0 && (
            <div className="cr-empty">
              <div className="cr-empty-icon">⭐</div>
              <h3>No reviews yet</h3>
              <p>When customers review your products, they'll appear here.</p>
            </div>
          )}
          {!loading && ratingFiltered.map(review => (
            <ReviewCard key={review.id} review={review} />
          ))}
        </main>
      </div>
    </div>
  );
}